import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import { useExamTypes } from '../../stateManagers/hooks/useExamTypes';
import useClassesStore from '../../stateManagers/store/useClassesStore';
import { useAuthStore } from '../../stateManagers/store/authStore';
import AddExamDateModal from './parts/AddExamDateModal';
import DeleteExamModal from './parts/DeleteExamModal';
import { useSchoolDetailsStore } from '../../stateManagers/hooks/useSchoolDetailsStore';

interface ExamDateSheet {
  _id: string;
  class_id: string;
  class_name: string;
  sections: string[];
  subject_id: string;
  subject_name: string;
  exam_date: string;
  exam_type_id: string;
  exam_type_name: string;
  exam_type_title: string;
  session: string;
  session_id: string;
  status: string;
}

interface TableRow {
  exam_date: string;
  date_display: string;
  day_display: string;
  [key: string]: any;
}

const DateSheet: React.FC = () => {
  const toast = useRef<Toast>(null);

  // ── Get school ID from auth store (JWT) ──────────────────────────────────
  const { user } = useAuthStore();
  const schoolId = user?.schoolId || '';

  // ── Fetch school details for name and session ─────────────────────────────
  const { schoolDetails, fetchSchoolDetails } = useSchoolDetailsStore();

  useEffect(() => {
    if (schoolId && !schoolDetails) {
      fetchSchoolDetails(schoolId);
    }
  }, [schoolId, schoolDetails, fetchSchoolDetails]);

  const currentSession = schoolDetails?.current_session?.session || '2025-2026';
  const currentSessionId = schoolDetails?.current_session?.id || '';

  const [examDates, setExamDates] = useState<ExamDateSheet[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCell, setSelectedCell] = useState<any>(null);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const {
    examTypes,
    loading: examTypesLoading,
    error: examTypesError,
    selectedExamTypeId,
    setSelectedExamTypeId,
  } = useExamTypes();

  const { classOptions, fetchClasses, isFetching: isFetchingClasses, hasFetched } = useClassesStore();

  useEffect(() => {
    if (!hasFetched && !isFetchingClasses) {
      fetchClasses(false);
    }
  }, [hasFetched, isFetchingClasses, fetchClasses]);

  const selectedExamType = examTypes.find((t) => t.value === selectedExamTypeId);

  // ── FETCH EXAM DATESHEETS ──────────────────────────────────────────────────
  const fetchExamDates = async () => {
    if (!selectedExamTypeId) {
      setExamDates([]);
      return;
    }

    setLoading(true);
    try {
      // ✅ school_id is resolved from JWT by backend - no need to send it
      const response = await apiClient.get('/api/exam-datesheets/by-type', {
        params: { 
          exam_type_id: selectedExamTypeId,
          session_id: currentSessionId,
        },
      });

      if (response.data.status === 'success') {
        setExamDates(response.data.data.items || []);
      } else {
        throw new Error(response.data.message || 'Failed to load exam schedule');
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to fetch exam dates';
      toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 5000 });
      setExamDates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExamDates();
  }, [selectedExamTypeId, currentSessionId]);

  // ── HELPERS ─────────────────────────────────────────────────────────────────
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return {
      full: date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
      day: date.toLocaleDateString('en-US', { weekday: 'short' }),
    };
  };

  // ── UNIQUE DATES ───────────────────────────────────────────────────────────
  const uniqueDates = Array.from(new Set(examDates.map((e) => e.exam_date)))
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

  // ── CLASS COLUMNS ──────────────────────────────────────────────────────────
  const classColumns = classOptions
    .filter((cls) => cls.id && cls.id !== '')
    .map((cls) => ({
      class_id: cls.id,
      class_name: cls.label,
    }));

  // ── GET EXAM FOR CELL ──────────────────────────────────────────────────────
  const getExamForCell = (date: string, classId: string) => {
    return examDates.find((e) => e.exam_date === date && e.class_id === classId) || null;
  };

  // ── TABLE DATA ─────────────────────────────────────────────────────────────
  const tableData: TableRow[] = uniqueDates.map(date => {
    const { full, day } = formatDate(date);
    const row: TableRow = {
      exam_date: date,
      date_display: full,
      day_display: day
    };

    classColumns.forEach(cls => {
      row[`class_${cls.class_id}`] = getExamForCell(date, cls.class_id);
    });

    return row;
  });

  // ── PRINT ──────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.current?.show({ severity: 'warn', summary: 'Blocked', detail: 'Popup blocked. Allow popups to print.' });
      return;
    }

    const title = selectedExamType?.label || 'Exam Date Sheet';
    const schoolName = schoolDetails?.name || 'School';

    let tableHTML = `
      <h1 style="text-align:center; font-size:24px; margin:20px 0;">${schoolName}</h1>
      <h2 style="text-align:center; font-size:20px; margin:10px 0; color:#1e40af;">${title}</h2>
      <p style="text-align:center; color:#666; margin-bottom:30px;">
        Session: ${currentSession} • Generated on: ${new Date().toLocaleString('en-GB')}
      </p>
      <table style="width:100%; border-collapse:collapse; font-family:Arial,sans-serif; font-size:12px;">
        <thead>
          <tr style="background:#f0f9ff;">
            <th style="border:1px solid #ccc; padding:12px; text-align:center;">Date</th>
            ${classColumns.map((c) => `<th style="border:1px solid #ccc; padding:12px; text-align:center;">${c.class_name}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
    `;

    uniqueDates.forEach((date) => {
      const { full, day } = formatDate(date);
      tableHTML += `
        <tr>
          <td style="border:1px solid #ccc; padding:12px; text-align:center; background:#f8fafc;">
            <strong>${full}</strong><br><small style="color:#666;">${day}</small>
          </td>
          ${classColumns.map((col) => {
            const exam = getExamForCell(date, col.class_id);
            return `
              <td style="border:1px solid #ccc; padding:12px; text-align:center;">
                ${exam ? `<strong>${exam.subject_name}</strong><br><small style="color:#555;">${exam.sections.join(', ')}</small>` : '—'}
              </td>
            `;
          }).join('')}
        </tr>
      `;
    });

    tableHTML += `</tbody></table>`;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head><title>${title}</title></head>
        <body style="padding:20px;">${tableHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // ── TEMPLATES ──────────────────────────────────────────────────────────────
  const dateColumnTemplate = (rowData: TableRow) => {
    return (
      <div style={{ textAlign: 'center', padding: '12px 8px' }}>
        <div style={{
          fontWeight: '600',
          fontSize: '13px',
          color: '#1f2937',
          marginBottom: '4px'
        }}>
          {rowData.date_display}
        </div>
        <div style={{
          fontSize: '11px',
          color: '#9ca3af',
          fontWeight: '500'
        }}>
          {rowData.day_display}
        </div>
      </div>
    );
  };

  const classColumnTemplate = (classId: string) => {
    return (rowData: TableRow) => {
      const exam = rowData[`class_${classId}`];

      return (
        <div
          onClick={() => {
            const classInfo = classColumns.find(c => c.class_id === classId);
            setSelectedCell({
              date: rowData.exam_date,
              classId: classId,
              className: classInfo?.class_name || '',
              exam
            });
            if (exam) {
              setDeleteDialogVisible(true);
            } else {
              setAddModalOpen(true);
            }
          }}
          style={{
            padding: '16px 12px',
            cursor: 'pointer',
            transition: 'background 0.2s ease',
            background: exam ? 'rgba(239, 246, 255, 0.6)' : 'transparent',
            minHeight: '80px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = exam ? '#e0f2fe' : '#f9fafb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = exam ? 'rgba(239, 246, 255, 0.6)' : 'transparent';
          }}
        >
          {exam ? (
            <>
              <div style={{
                fontWeight: '600',
                fontSize: '13px',
                color: '#1e40af',
                textAlign: 'center'
              }}>
                {exam.subject_name}
              </div>
              {exam.sections && exam.sections.length > 0 && (
                <Tag
                  value={exam.sections.join(', ')}
                  severity="info"
                  style={{
                    fontSize: '11px',
                    padding: '3px 8px',
                    background: '#dbeafe',
                    color: '#1e40af',
                    border: '1px solid #93c5fd'
                  }}
                />
              )}
            </>
          ) : (
            <span style={{
              fontSize: '12px',
              color: '#cbd5e1',
              fontWeight: '500'
            }}>
              —
            </span>
          )}
        </div>
      );
    };
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <div style={{ minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif', maxWidth: '100%', overflowX: 'hidden' }}>
        <style>{`
          .chip-button {
            transition: all 0.2s ease;
          }
          .chip-button:hover:not(:disabled) {
            transform: translateY(-1px);
          }
          .chip-button:active:not(:disabled) {
            transform: translateY(0);
          }
          .p-datatable-thead > tr > th {
            background: #f9fafb !important;
            color: #6b7280 !important;
            font-size: 12px !important;
            font-weight: 700 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.5px !important;
            padding: 16px 12px !important;
            border: 1px solid #e5e7eb !important;
            text-align: center !important;
            white-space: nowrap !important;
          }
          .p-datatable-tbody > tr > td {
            padding: 0 !important;
            border: 1px solid #e5e7eb !important;
            vertical-align: middle !important;
          }
          .p-datatable-tbody > tr {
            transition: none !important;
          }
          .p-datatable-tbody > tr:hover {
            background: transparent !important;
          }
          .action-btn {
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .action-btn:hover {
            transform: translateY(-2px);
          }
          .date-sheet-scroll::-webkit-scrollbar {
            height: 10px;
          }
          .date-sheet-scroll::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .date-sheet-scroll::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .date-sheet-scroll::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
          .p-datatable-wrapper::-webkit-scrollbar {
            height: 10px;
            width: 10px;
          }
          .p-datatable-wrapper::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .p-datatable-wrapper::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .p-datatable-wrapper::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
          .p-datatable-wrapper {
            overflow: auto !important;
          }
          .p-datatable-frozen-column {
            position: sticky !important;
            left: 0 !important;
            z-index: 1 !important;
            background: white !important;
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          @media (max-width: 768px) {
            .header-content {
              flex-direction: column !important;
              align-items: flex-start !important;
              gap: 16px !important;
            }
            .header-buttons {
              width: 100%;
              justify-content: stretch !important;
            }
            .header-buttons button {
              flex: 1;
            }
          }
        `}</style>

        <GlobalHeaderTitle
          title="Exam Date Sheet"
          subtitle={selectedExamType ? `${selectedExamType.label} • ${uniqueDates.length} dates • ${classColumns.length} classes • ${currentSession}` : 'Select exam type to view schedule'}
          icon="pi pi-calendar"
          buttons={[
            {
              label: "",
              icon: "pi pi-print",
              onClick: handlePrint,
              outlined: true,
              disabled: !selectedExamTypeId || loading || examDates.length === 0,
              style: { borderWidth: '2px' }
            },
            {
              label: "Add Exam",
              icon: "pi pi-plus",
              onClick: () => setAddModalOpen(true),
              disabled: !selectedExamTypeId
            }
          ]}
        />

        {/* ── Exam Type Filter ── */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '16px 20px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
          border: '1px solid rgba(59, 130, 246, 0.12)',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '600',
                color: '#1e40af',
                marginBottom: '6px',
                letterSpacing: '0.02em'
              }}>
                Select Exam Type
              </label>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                {examTypesLoading ? (
                  <div style={{
                    padding: '6px 14px',
                    color: '#64748b',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}>
                    Loading exam types...
                  </div>
                ) : examTypesError ? (
                  <div style={{
                    padding: '6px 14px',
                    color: '#ef4444',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}>
                    {examTypesError}
                  </div>
                ) : examTypes.length === 0 ? (
                  <div style={{
                    padding: '6px 14px',
                    color: '#64748b',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}>
                    No exam types available
                  </div>
                ) : (
                  examTypes.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setSelectedExamTypeId(type.value)}
                      className="chip-button"
                      style={{
                        padding: '6px 14px',
                        border: selectedExamTypeId === type.value
                          ? '1.5px solid #3b82f6'
                          : '1.5px solid #e5e7eb',
                        borderRadius: '8px',
                        background: selectedExamTypeId === type.value
                          ? 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)'
                          : 'white',
                        color: selectedExamTypeId === type.value ? 'white' : '#1f2937',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        outline: 'none',
                        boxShadow: selectedExamTypeId === type.value
                          ? '0 2px 8px rgba(59, 130, 246, 0.25)'
                          : 'none',
                      }}
                    >
                      {type.label}
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* ── School & Session Info ── */}
            {schoolDetails && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '6px 14px',
                background: 'rgba(59, 130, 246, 0.06)',
                borderRadius: '10px',
                border: '1px solid rgba(59, 130, 246, 0.12)',
                flexShrink: 0
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <i className="pi pi-building" style={{ fontSize: '12px', color: '#3b82f6' }} />
                  <span style={{ fontSize: '12px', color: '#1e40af', fontWeight: '600' }}>
                    {schoolDetails.name}
                  </span>
                </div>
                <div style={{
                  width: '1px',
                  height: '20px',
                  background: 'rgba(59, 130, 246, 0.2)'
                }} />
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <i className="pi pi-calendar" style={{ fontSize: '12px', color: '#3b82f6' }} />
                  <span style={{ fontSize: '12px', color: '#1e40af', fontWeight: '600' }}>
                    {currentSession}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Table ── */}
        <div style={{
          background: 'white',
          borderRadius: '20px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
          width: '100%',
          maxHeight: 'calc(100vh - 280px)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {isFetchingClasses || loading ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '80px 20px',
              minHeight: '400px'
            }}>
              <div style={{
                width: '56px',
                height: '56px',
                background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
                boxShadow: '0 6px 18px rgba(59, 130, 246, 0.2)',
                animation: 'pulse 2s ease-in-out infinite'
              }}>
                <i className="pi pi-spin pi-spinner" style={{ fontSize: '25px', color: 'white' }}></i>
              </div>
              <h3 style={{
                margin: 0,
                color: '#1e40af',
                fontSize: '18px',
                fontWeight: '700',
                marginBottom: '8px'
              }}>
                Loading Schedule...
              </h3>
              <p style={{
                margin: 0,
                color: '#64748b',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Please wait while we fetch the exam dates
              </p>
            </div>
          ) : !selectedExamTypeId ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '80px 20px',
              minHeight: '400px'
            }}>
              <div style={{
                width: '56px',
                height: '56px',
                background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
                boxShadow: '0 6px 18px rgba(59, 130, 246, 0.2)',
              }}>
                <i className="pi pi-filter" style={{ fontSize: '25px', color: 'white' }}></i>
              </div>
              <h3 style={{
                margin: 0,
                color: '#1e40af',
                fontSize: '20px',
                fontWeight: '700',
                marginBottom: '8px',
                textAlign: 'center'
              }}>
                No Exam Type Selected
              </h3>
              <p style={{
                margin: 0,
                color: '#64748b',
                fontSize: '14px',
                fontWeight: '500',
                textAlign: 'center',
                maxWidth: '400px',
                lineHeight: 1.6
              }}>
                Please select an exam type from the filter above to view the schedule
              </p>
            </div>
          ) : classColumns.length === 0 || examDates.length === 0 ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '80px 20px',
              minHeight: '400px'
            }}>
              <div style={{
                width: '56px',
                height: '56px',
                background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
                boxShadow: '0 6px 18px rgba(59, 130, 246, 0.2)',
              }}>
                <i className="pi pi-inbox" style={{ fontSize: '25px', color: 'white' }}></i>
              </div>
              <h3 style={{
                margin: 0,
                color: '#1e40af',
                fontSize: '20px',
                fontWeight: '700',
                marginBottom: '8px',
                textAlign: 'center'
              }}>
                No Schedule Available
              </h3>
              <p style={{
                margin: 0,
                color: '#64748b',
                fontSize: '14px',
                fontWeight: '500',
                textAlign: 'center',
                maxWidth: '400px',
                lineHeight: 1.6
              }}>
                {classColumns.length === 0
                  ? 'No classes found. Please add classes first.'
                  : 'No exam dates scheduled yet. Click "Add Exam" to create a new entry.'}
              </p>
            </div>
          ) : (
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <DataTable
                value={tableData}
                loading={false}
                showGridlines
                scrollable
                scrollHeight="flex"
                tableStyle={{ minWidth: '50rem' }}
              >
                <Column
                  field="date_display"
                  header="Date"
                  body={dateColumnTemplate}
                  style={{ minWidth: '140px' }}
                  frozen
                  alignFrozen="left"
                />
                {classColumns.map((cls) => (
                  <Column
                    key={cls.class_id}
                    header={cls.class_name}
                    body={classColumnTemplate(cls.class_id)}
                    style={{ minWidth: '180px' }}
                  />
                ))}
              </DataTable>
            </div>
          )}
        </div>
      </div>

      <DeleteExamModal
        deleteDialogVisible={deleteDialogVisible}
        setDeleteDialogVisible={setDeleteDialogVisible}
        selectedCell={selectedCell}
        setSelectedCell={setSelectedCell}
        fetchExamDates={fetchExamDates}
        toast={toast}
      />

      <AddExamDateModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={() => {
          fetchExamDates();
        }}
        currentSession={currentSession}
        currentSessionId={currentSessionId}
      />
    </>
  );
};

export default DateSheet;